import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';

export interface UserProfile {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phone?: string;
  role: string;
  tenantId: string;
  emailVerified: boolean;
  createdAt: string;
  lastLoginAt?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: UserProfile | null;
  loading: boolean;
  error: string | null;
  
  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  fetchProfile: () => Promise<void>;
  clearError: () => void;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role?: string;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      loading: false,
      error: null,

      login: async (email: string, password: string) => {
        try {
          set({ loading: true, error: null });
          
          // Call PM Identity login endpoint
          await api.post('/api/pm/auth/login', {
            email,
            password,
          });
          
          // Fetch user profile
          await get().fetchProfile();
          
          set({ isAuthenticated: true, loading: false });
        } catch (err: any) {
          const message = err.response?.data?.message || err.message || 'Login failed';
          set({ error: message, loading: false, isAuthenticated: false });
          throw err;
        }
      },

      register: async (data: RegisterData) => {
        try {
          set({ loading: true, error: null });
          
          // Call PM Identity register endpoint
          await api.post('/api/pm/auth/register', {
            email: data.email,
            password: data.password,
            firstName: data.firstName,
            lastName: data.lastName,
            phone: data.phone,
            role: data.role || 'Landlord',
          });
          
          // Fetch user profile after registration
          await get().fetchProfile();
          
          set({ isAuthenticated: true, loading: false });
        } catch (err: any) {
          const message = err.response?.data?.message || err.message || 'Registration failed';
          set({ error: message, loading: false, isAuthenticated: false });
          throw err;
        }
      },

      logout: async () => {
        try {
          // Call PM Identity logout to clear cookies
          await api.post('/api/pm/auth/logout');
        } catch (err) {
          console.warn('Backend logout failed:', err);
        }
        
        // Clear local state
        localStorage.removeItem('accessToken');
        set({ isAuthenticated: false, user: null, error: null });
        
        // Redirect to login
        window.location.href = '/login';
      },

      fetchProfile: async () => {
        try {
          set({ loading: true });
          const { data } = await api.get('/api/pm/auth/me');
          set({ user: data, isAuthenticated: true, loading: false });
        } catch (err: any) {
          console.error('Failed to fetch profile:', err);
          set({ loading: false });
          // Don't set error here - might just be not logged in
          if (err.response?.status === 401) {
            set({ isAuthenticated: false, user: null });
          }
          throw err;
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'pm-auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        // Don't persist user data for security
      }),
    }
  )
);
